import React, { useMemo, useState } from 'react';
import { MdArrowBack, MdSearch, MdClose } from 'react-icons/md';
import { useProfile } from '../contexts/ProfileContext';
import { denormalizeTransactions } from '../utils/denormalizeTransactions';

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// How many recent transactions to show across all accounts when no query is
// active — keeps the initial list readable without loading full history.
const RECENT_LIMIT = 40;

const SearchScreen = ({ onNavigate, onBack }) => {
  const { loadedProfileData } = useProfile();
  const [searchQuery, setSearchQuery] = useState('');

  // Flatten every account's transactions into one array, tagging each with the
  // account it came from, then sort by date descending.
  const allTxns = useMemo(() => {
    const accounts = loadedProfileData?.profile?.accounts;
    if (!accounts) return null;

    const flat = [];
    Object.entries(accounts).forEach(([accountId, acct]) => {
      const acctName = acct.account_type || acct.name || acct.type || accountId;
      denormalizeTransactions(acct.transactions)
        .filter(t => t.date)
        .forEach(t => flat.push({ ...t, _acct: acctName }));
    });
    return flat.sort((a, b) => b.date.localeCompare(a.date));
  }, [loadedProfileData]);

  // Apply the query (or fall back to the most recent slice), then group by date.
  const groupedTxns = useMemo(() => {
    if (!allTxns) return null;
    const q = searchQuery.trim().toLowerCase();

    const visible = q
      ? allTxns.filter(t =>
          (t.description || '').toLowerCase().includes(q) ||
          (t.category || '').toLowerCase().includes(q) ||
          // numeric substring match on the (absolute) amount
          Math.abs(t.amount).toString().includes(q))
      : allTxns.slice(0, RECENT_LIMIT);

    const byDate = {};
    visible.forEach(t => {
      if (!byDate[t.date]) byDate[t.date] = [];
      byDate[t.date].push(t);
    });

    return Object.entries(byDate)
      .sort(([a], [b]) => b.localeCompare(a))
      .map(([dateStr, txns]) => {
        const [, m, d] = dateStr.split('-');
        const label = `${MONTH_NAMES[parseInt(m, 10) - 1]} ${parseInt(d, 10)}`;
        return {
          dateGroup: label,
          items: txns.map(t => ({
            id: (t.id || t.date + t.description) + '|' + t._acct,
            merchant: t.description,
            category: t.category,
            account: t._acct,
            amount: (t.amount < 0 ? '-' : '+') + '$' + Math.abs(t.amount).toLocaleString('en-CA', { minimumFractionDigits: 2 }),
            isPositive: t.amount > 0,
          })),
        };
      });
  }, [allTxns, searchQuery]);

  const matchedCount = useMemo(() => {
    if (!groupedTxns) return null;
    return groupedTxns.reduce((s, g) => s + g.items.length, 0);
  }, [groupedTxns]);

  const header = (
    <header className="bh-flow-hero">
      <div className="bh-flow-hero-left">
        <button className="bh-flow-back" onClick={onBack || (() => onNavigate('home'))} aria-label="Back">
          <MdArrowBack />
        </button>
        <h1 className="bh-flow-title">Search</h1>
      </div>
    </header>
  );

  if (!allTxns) {
    return (
      <div className="screen bh-flow">
        {header}
        <div className="bh-flow-body">
          <div className="bh-form-card" style={{ alignItems: 'center' }}>
            <p style={{ margin: 0, color: 'var(--bh-gray)', font: 'var(--bh-body-sm)' }}>
              Load a profile to search across your accounts.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const hasQuery = searchQuery.trim().length > 0;

  return (
    <div className="screen search-screen">
      <div className="scroll-content">
        {header}
        <div className="bh-flow-body">
          {/* Search input — mirrors the AccountDetailsScreen search UX. */}
          <div className="bh-form-card" style={{ padding: 'var(--space-2xs) var(--space-sm)', gap: 'var(--space-3xs)' }}>
            <div style={{ position: 'relative' }}>
              <MdSearch size={18} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--bh-gray)', pointerEvents: 'none' }} />
              <input
                className="bh-field-input"
                type="search"
                placeholder="Search all accounts by merchant, category, or amount"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{ paddingLeft: 36, paddingRight: searchQuery ? 36 : 12 }}
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => setSearchQuery('')}
                  aria-label="Clear search"
                  style={{ position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)', background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--bh-gray)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: 6 }}
                >
                  <MdClose size={18} />
                </button>
              )}
            </div>
            <span style={{ font: 'var(--bh-caption)', color: 'var(--bh-gray)' }}>
              {hasQuery
                ? `${matchedCount} match${matchedCount === 1 ? '' : 'es'} across all accounts`
                : `Showing ${matchedCount} most recent across all accounts`}
            </span>
          </div>

          {matchedCount > 0 ? (
            <div className="bh-tx-list">
              {groupedTxns.map((group, index) => (
                <React.Fragment key={index}>
                  <div className="bh-tx-group-title">{group.dateGroup}</div>
                  {group.items.map(item => (
                    <div key={item.id} className="bh-tx-row">
                      <div className="bh-tx-row-info">
                        <span className="bh-tx-merchant">{item.merchant}</span>
                        <span className="bh-tx-date" style={{ font: 'var(--bh-caption)', color: 'var(--bh-gray)' }}>
                          {item.account}{item.category ? ` · ${item.category}` : ''}
                        </span>
                      </div>
                      <span className={`bh-tx-amount${item.isPositive ? ' positive' : ''}`}>
                        {item.amount}
                      </span>
                    </div>
                  ))}
                </React.Fragment>
              ))}
            </div>
          ) : (
            <div className="bh-form-card" style={{ alignItems: 'center' }}>
              <p style={{ margin: 0, color: 'var(--bh-gray)', font: 'var(--bh-body-sm)' }}>
                {hasQuery ? `No transactions match "${searchQuery}"` : 'No transactions found'}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SearchScreen;
